import { useCallback, useRef, useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';

import { cn } from '@/lib/utils';

import { useDateRangePicker, type DateRangeValue } from '../model/use-date-range-picker';
import { DateRangeTrigger } from './date-range-trigger';
import { PresetSidebar } from './preset-sidebar';
import { CalendarSection } from './calendar-section';

interface DateRangePickerProps {
  label?: string;
  defaultPreset?: string;
  defaultFromDate?: string;
  defaultToDate?: string;
  onChange?: (value: DateRangeValue) => void;
  /**
   * Overrides the picker's default 260px width. The trigger fills whatever the
   * root is given, so `w-full` here really does stretch the control — passing it
   * only to the root used to leave a 260px button inside a full-width box.
   */
  className?: string;
}

export function DateRangePicker({
  label,
  defaultPreset = 'L6M',
  defaultFromDate,
  defaultToDate,
  onChange,
  className
}: DateRangePickerProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [container, setContainer] = useState<HTMLElement | null>(null);

  const {
    open,
    dateRange,
    selectedPreset,
    presetRanges,
    setOpen,
    handleRangeChange,
    handlePresetSelect,
    handleCancel,
    handleApply
  } = useDateRangePicker({
    defaultPreset,
    defaultFromDate,
    defaultToDate,
    onChange
  });

  /**
   * Radix portals the popover to `document.body`. Inside the mobile filter
   * drawer that lands outside vaul's scroll lock (Radix Dialog +
   * react-remove-scroll), which then swallows wheel and touch events over the
   * preset row — the row shows a scrollbar that cannot be moved. Portalling
   * into the drawer instead keeps the popover inside the lock's own subtree.
   * Anywhere else `closest` finds nothing and the portal stays on the body.
   */
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next) {
        setContainer(rootRef.current?.closest<HTMLElement>('[data-vaul-drawer-direction]') ?? null);
      }
      setOpen(next);
    },
    [setOpen]
  );

  return (
    <div ref={rootRef} className={cn('flex w-[260px] flex-col gap-2', className)}>
      {label && <Label>{label}</Label>}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <DateRangeTrigger dateRange={dateRange} />
        </PopoverTrigger>
        <PopoverContent
          container={container ?? undefined}
          className="w-auto p-0 max-w-[calc(100vw-2rem)] sm:max-w-none"
          align="start">
          <div className="flex flex-col sm:flex-row max-h-[calc(100vh-8rem)] overflow-y-auto sm:max-h-none sm:overflow-visible">
            <PresetSidebar
              presetRanges={presetRanges}
              selectedPreset={selectedPreset}
              onPresetSelect={handlePresetSelect}
            />
            <CalendarSection
              dateRange={dateRange}
              onRangeChange={handleRangeChange}
              handleCancel={handleCancel}
              handleApply={handleApply}
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
